import useWorkOrdersColumns from '@/components/Movements/WorkOrders/List/useWorkOrdersColumns';
import {
  Group,
  InfiniteTable,
  LicensePlateFormField,
} from '@/components/_common';
import { useWorkOrders } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Alert, Button, Form, Modal } from 'antd';
import { FaEdit, FaPlus } from 'react-icons/fa';
import VisionCustomerDetail from '../VisionCustomerDetail';
import ActionIcon from '@/components/_common/ActionIcon';
import { useDisclosure } from '@/hooks';
import { useEffect, useState } from 'react';
import { useVehiclesFilterReduced } from '@/core/domain/people/Customers/Customers.hooks';
import VisionWorkorderCreate from '../VisionWorkorderCreate';
import SpaceForButton from '@/components/_common/SpaceForButtons';

interface Props {
  licensePlate?: string;
}

export default function VisionWorkorders(props: Props) {
  const [licensePlate, setLicensePlate] = useState<string | undefined>();

  const [form] = Form.useForm();

  const workorderList = useWorkOrders({ licensePlate }, !!licensePlate);
  const { columns, isFetching } = useWorkOrdersColumns();

  const loading = isFetching || workorderList.isLoading;

  const workorders = flatInfiniteData(workorderList.data);

  const [editPlateOpened, editPlateAction] = useDisclosure();
  const [createOSOpened, createOSAction] = useDisclosure();

  const vehicleList = useVehiclesFilterReduced(
    { licensePlate: licensePlate },
    !!licensePlate,
  );

  const vehicle = flatInfiniteData(vehicleList.data)[0];

  useEffect(() => {
    setLicensePlate(props.licensePlate);
  }, [props.licensePlate]);

  return (
    <>
      {!!licensePlate && (
        <>
          <div style={{ marginBottom: 24 }}>
            <div>Histórico de orçamentos do veículo</div>
            <Group gap={8}>
              <strong>{licensePlate}</strong>

              <ActionIcon onClick={editPlateAction.open}>
                <FaEdit />
              </ActionIcon>
            </Group>
          </div>

          <div style={{ marginBottom: 40 }}>
            <VisionCustomerDetail
              vehicle={vehicle}
              licensePlate={licensePlate}
            />
          </div>

          {!!vehicle && (
            <Group
              position="between"
              gap={16}
              style={{ marginBottom: 40, flexWrap: 'wrap' }}
            >
              <strong style={{ fontSize: 18 }}>Orçamentos</strong>

              {!!workorders.length && (
                <Button block type="primary" onClick={createOSAction.open}>
                  <Group position="center">
                    <FaPlus />
                    <span>Orçamento rápido</span>
                  </Group>
                </Button>
              )}
            </Group>
          )}

          {!workorders.length && !workorderList.isLoading && !!vehicle && (
            <Alert
              style={{ borderRadius: 8 }}
              type="warning"
              message={<strong>Não há orçamento</strong>}
              description={
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
                >
                  <span>
                    Parece que este veículo é novo, gostaria de abrir um
                    orçamento para o veículo?
                  </span>

                  <Group gap={24}>
                    <Button type="primary" onClick={createOSAction.open}>
                      <Group>
                        <FaPlus />
                        <span>Orçamento rápido</span>
                      </Group>
                    </Button>
                  </Group>
                </div>
              }
            />
          )}

          {!!workorders.length && !!vehicle && (
            <InfiniteTable
              loading={loading}
              rowKey={(row) => row.complementId}
              dataSource={workorders}
              columns={columns}
            />
          )}

          <Modal
            open={editPlateOpened}
            onCancel={editPlateAction.close}
            destroyOnClose
            footer={
              <SpaceForButton justifyContent="flex-end">
                <Button block onClick={editPlateAction.close}>
                  Cancelar
                </Button>

                <Button
                  type="primary"
                  block
                  onClick={async () => {
                    try {
                      const values = await form.validateFields();
                      setLicensePlate(values.licensePlate);
                      editPlateAction.close();
                    } catch {}
                  }}
                >
                  Ajustar placa
                </Button>
              </SpaceForButton>
            }
          >
            <div>
              <strong>Ajuste de placa</strong>
            </div>

            <p>
              O processo de leitura pode apresentar inconsistências devido à
              qualidade da imagem ou às condições da placa.
            </p>

            <Form form={form} preserve={false}>
              <LicensePlateFormField
                name="licensePlate"
                label="Placa"
                required
                initialValue={licensePlate}
              />
            </Form>
          </Modal>

          <VisionWorkorderCreate
            opened={createOSOpened}
            onClose={createOSAction.close}
            vehicle={vehicle}
          />
        </>
      )}
    </>
  );
}
